import React, { FC, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import GuestLayout from '../../components/layouts/guest_layout'
import FlatButton from '../../components/buttons/flat_button'
import { useLoading } from '../../providers/loading_provider'
import { useSnackBar } from '../../providers/snack_bar_provider'
import { getKyousouApiClient, handleError, ApiErrorResponse } from '../../services/network'
import {
  FormField,
} from '../../components/form'

type ContactFormProps = {
  enterpriseName: string | null
  departmentName: string | null
  name: string
  email: string
  tel: string | null
  subject: string
  message: string
  privacyPolicyAgreed: boolean
}

const ContactConfirmPage: FC = () => {
  const location = useLocation()
  const state = location.state as ContactFormProps
  const { showSnackBar } = useSnackBar()
  const { showLoading, hideLoading } = useLoading()
  const navigate = useNavigate()

  // ページ読み込み時にトップにスクロール
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // 状態がない場合はお問い合わせページに戻す
  useEffect(() => {
    if (!state) {
      navigate('/contact')
    }
  }, [state, navigate])

  const handleSend = async () => {
    showLoading()
    try {
      const apiClient = getKyousouApiClient()
      await apiClient.contact.sendCreate({
        enterpriseName: state.enterpriseName,
        departmentName: state.departmentName,
        name: state.name,
        email: state.email,
        tel: state.tel,
        subject: state.subject,
        message: state.message,
      })

      showSnackBar(['お問い合わせを送信しました。'], 'success')
      navigate('/contact/complete')
    } catch (error) {
      handleError(error as ApiErrorResponse, showSnackBar)
    } finally {
      hideLoading()
    }
  }

  if (!state) {
    return null
  }

  return (
    <GuestLayout>
      <div className="max-w-5xl w-full mx-auto">
        <div className="px-4 sm:px-8 py-8">
          <h1 className="font-bold text-2xl mb-6">お問い合わせ内容確認</h1>
          
          <div className="bg-main-white rounded-xl py-8">
            <div className="max-w-3xl mx-auto px-4 sm:px-8">
              <p className="mb-8 text-main-darkgray">
                以下の内容でお問い合わせを送信します。内容に問題なければ確定して送信するボタンを押してください。
              </p>
              
              {/* 企業名・団体名 */}
              {state.enterpriseName && (
                <FormField label="企業名・団体名" required={false}>
                  <div className="mt-1 px-3 py-2 bg-main-lightgray rounded-md">
                    {state.enterpriseName}
                  </div>
                </FormField>
              )}

              {/* 部署名 */}
              {state.departmentName && (
                <FormField label="部署名" required={false}>
                  <div className="mt-1 px-3 py-2 bg-main-lightgray rounded-md">
                    {state.departmentName}
                  </div>
                </FormField>
              )}

              {/* 名前 */}
              <FormField label="名前" required>
                <div className="mt-1 px-3 py-2 bg-main-lightgray rounded-md">
                  {state.name}
                </div>
              </FormField>

              {/* メールアドレス */}
              <FormField label="メールアドレス" required>
                <div className="mt-1 px-3 py-2 bg-main-lightgray rounded-md">
                  {state.email}
                </div>
              </FormField>

              {/* 電話番号 */}
              {state.tel && (
                <FormField label="電話番号" required={false}>
                  <div className="mt-1 px-3 py-2 bg-main-lightgray rounded-md">
                    {state.tel}
                  </div>
                </FormField>
              )}

              {/* 件名 */}
              <FormField label="件名" required>
                <div className="mt-1 px-3 py-2 bg-main-lightgray rounded-md">
                  {state.subject}
                </div>
              </FormField>

              {/* お問い合わせ本文 */}
              <FormField label="お問い合わせ本文" required>
                <div className="mt-1 px-3 py-2 bg-main-lightgray rounded-md whitespace-pre-wrap">
                  {state.message}
                </div>
              </FormField>

              {/* プライバシーポリシー */}
              <div className="mt-8">
                <h3 className="font-bold text-lg mb-4">プライバシーポリシーについて</h3>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={state.privacyPolicyAgreed}
                    disabled
                    className="mr-2"
                  />
                  <span className="text-sm">
                    上記のプライバシーポリシーについて同意する
                  </span>
                </div>
              </div>

              {/* ボタン */}
              <div className="grid grid-cols-2 gap-6 mt-8 max-w-md mx-auto">
                <FlatButton
                  type="button"
                  onClick={() => navigate('/contact', { state })}
                  outline
                  className="py-3"
                >
                  内容を修正する
                </FlatButton>
                <FlatButton
                  type="button"
                  onClick={handleSend}
                  className="py-3"
                >
                  確定して送信する
                </FlatButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GuestLayout>
  )
}

export default ContactConfirmPage